import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";

// The admin reference: what everything in this system is, and what it is not.
//
// WHY A SCREEN AND NOT ONLY A FILE. `docs/GLOSSARY.md` is generated from the same source and is
// what anyone reading the repo sees. This is the same content where the questions get asked — you
// are looking at a folder of work wondering what an occasion is, and the answer should not require
// finding the repository.
//
// Admin-only, deliberately. A teacher meets these words one at a time, in place, through the
// tooltips on the words themselves. A list of twenty-one definitions is a reference for whoever
// maintains the thing, and putting it in a teacher's sidebar would advertise our internals as
// somewhere they ought to go.

interface Entry {
  term: string;
  also?: string[];
  gloss: string;
  is: string;
  is_not: string;
  lives_in: string[];
  relates_to: string[];
}

const slug = (term: string) => `gl-${term.replace(/\s+/g, "-")}`;

export function GlossaryPage() {
  const [terms, setTerms] = useState<Entry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get<{ terms: Entry[] }>("/glossary")
      .then((v) => setTerms([...(v.terms ?? [])].sort((a, b) => a.term.localeCompare(b.term))))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "could not be read"));
  }, []);

  const shown = useMemo(() => {
    if (!terms) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return terms;
    // Searches the definitions too, not just the names: you usually arrive with the thing you are
    // confused about rather than with the word for it.
    return terms.filter((t) =>
      [t.term, ...(t.also ?? []), t.gloss, t.is, t.is_not].join(" ").toLowerCase()
        .includes(needle));
  }, [terms, q]);

  if (error) return <div className="gl"><p className="inc-error">{error}</p></div>;
  if (!terms) return <div className="gl muted dots">loading the glossary</div>;

  return (
    <div className="gl">
      <h2>Glossary</h2>
      <p className="gl-lede">
        The words this system uses, and what each one means.
      </p>

      <input className="gl-find" type="search" value={q} placeholder="find a term or a phrase"
             onChange={(e) => setQ(e.target.value)} aria-label="find a term" />

      <nav className="gl-index" aria-label="every term">
        {terms.map((t) => (
          <a key={t.term} href={`#${slug(t.term)}`}>{t.term}</a>
        ))}
      </nav>

      {shown.length === 0 && <p className="rv-mut">Nothing here matches “{q}”.</p>}

      {shown.map((t) => (
        <section className="gl-entry" id={slug(t.term)} key={t.term}>
          <h3>
            {t.term}
            {t.also?.length ? <span className="gl-also">also: {t.also.join(", ")}</span> : null}
          </h3>
          <p className="gl-gloss">{t.gloss}</p>
          <p>{t.is}</p>
          {/* The half that does the work. Most confusion here has been a word standing in for
              something next to it — a folder for a connection, an occasion for a rubric. */}
          <p className="gl-not"><b>What it is not.</b> {t.is_not}</p>
          <p className="gl-where">
            {t.lives_in.map((p) => <code key={p}>{p}</code>)}
          </p>
          {t.relates_to.length > 0 && (
            <p className="gl-see">
              <b>See also.</b>{" "}
              {t.relates_to.map((r, i) => (
                <span key={r}>
                  {i > 0 && ", "}
                  <a href={`#${slug(r)}`}>{r}</a>
                </span>
              ))}
            </p>
          )}
        </section>
      ))}
    </div>
  );
}
